import { useState, useCallback, useEffect } from 'react';

interface Track {
  id: string;
  name: string;
  artist: string;
  album: string;
  imageUrl: string;
  previewUrl: string | null;
  spotifyUrl: string;
  uri: string;
  durationMs?: number;
  youtubeId?: string;
}

interface UseTrackQueueOptions {
  isAutoPlayEnabled: boolean;
  currentTrack: Track | null;
  onPlayNext: (track: Track) => void;
}

export const useTrackQueue = ({ isAutoPlayEnabled, currentTrack, onPlayNext }: UseTrackQueueOptions) => {
  const [queue, setQueue] = useState<Track[]>([]);

  const addToQueue = useCallback((tracks: Track[]) => {
    setQueue(prev => [...prev, ...tracks]);
  }, []);

  const clearQueue = useCallback(() => {
    setQueue([]);
  }, []);

  const playNextInQueue = useCallback(() => {
    if (queue.length > 0) {
      const [nextTrack, ...remainingQueue] = queue;
      setQueue(remainingQueue);
      onPlayNext(nextTrack);
    }
  }, [queue, onPlayNext]);

  // Auto-play next track when queue is updated and no track is playing
  useEffect(() => {
    if (isAutoPlayEnabled && !currentTrack && queue.length > 0) {
      playNextInQueue();
    }
  }, [isAutoPlayEnabled, currentTrack, queue.length, playNextInQueue]);

  return {
    queue,
    addToQueue,
    clearQueue,
    playNextInQueue,
  };
};
